import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ICoreAuthorizationDTO, ICoreMemberDTO, IDocumentDetailsDTO, IDocumentReferenceProductDTO, IDocumentResponseDTO } from '@app/core/model';
import { DocumentsContextService } from '@app/core/services/context/factory';
import { PacimUploadFilesDialogComponent } from '@app/products/pac/components/upload/pacim-upload-files-dialog/pacim-upload-files-dialog.component';
import { SharedDocumentsUploadService } from '@app/shared/services/shared-documents-upload.service';
import { ShellService } from '@mynexus/mfe-core';
import { FeatureToggleService, LineOfBusiness, ProductNames } from '@mynexus/mynexus-ui-lib';
import { ToastMessageOptions } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, catchError, map, of } from 'rxjs';
import { AdditionalDaysRequestPendingSL, ChooseActionInfoMessage, ContinuedStayRequestTwoMoreDaysSL, SomethingWentWrong } from '../constants/document.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlatformService } from '@app/core/services/context/platform.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentsUploadPACService extends SharedDocumentsUploadService {
  approvalLetterOverdue = false;

  private readonly nomncRescind = 'NOMNC Rescind';
  private readonly levelAdjustment = 'Level Adjustment';
  private readonly businessDays = 10;
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformService = inject(PlatformService);
  private pacProductAndLobMigration = false;

  constructor(
    http: HttpClient,
    shellService: ShellService,
  ) {
    super(http, shellService, ProductNames.PACIM);
    
    this.featureToggleService.getFeatureAccess('PacProductAndLobMigration')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.pacProductAndLobMigration = isEnabled;
      });
  }

  override getModal(
    dialogService: DialogService,
    authorizationDto: ICoreAuthorizationDTO,
    lineData: object[],
    documents: IDocumentDetailsDTO[],
    memberDto: ICoreMemberDTO,
    message: ToastMessageOptions,
  ): DynamicDialogRef {
    const facilityType = authorizationDto.streamData['facilityType'];
    const isCommercial = this.pacProductAndLobMigration
      ? authorizationDto.streamData['productName'] === ProductNames.PACIM && authorizationDto.streamData['lineOfBusiness'] === LineOfBusiness.Commercial
      : authorizationDto.streamData['productName'] === ProductNames.PacCommercial;
    const hasLevelAdjustment = isCommercial ? facilityType !== 'IRF' : facilityType === 'SNF';

    const modalConfig = {
      header: 'Choose Next Action',
      width: '500px',
      modal: true,
      data: {
        serviceLines: lineData,
        isPacCommercial: isCommercial,
        documents: documents,
        hasLevelAction: hasLevelAdjustment && !this.isACMPApprovalLetterOverdue,
        programState: memberDto.streamData['programState'],
        message: message,
      },
    };

    return dialogService.open(PacimUploadFilesDialogComponent, modalConfig);
  }

  override levelAdjustmentUpload(selectedDocumentTypes: string[]): void {
    const levelAdjustment = 'Level Adjustment';
    if (!selectedDocumentTypes.includes(levelAdjustment)) {
      selectedDocumentTypes.push(levelAdjustment);
    }
  }

  set isACMPApprovalLetterOverdue(value: boolean) {
    this.approvalLetterOverdue = value;
  }

  get isACMPApprovalLetterOverdue(): boolean {
    return this.approvalLetterOverdue;
  }

  override manageForbiddenDocumentType(
    documents: IDocumentResponseDTO[],
    documentsContextService: DocumentsContextService,
    documentTypes: IDocumentReferenceProductDTO[],
    authorizationStatus: string,
    productName: string,
    servicingProvider: any,
    createContinuedStayRequest: boolean,
  ): Observable<IDocumentReferenceProductDTO[]> {
    const isCommercial = this.pacProductAndLobMigration
      ? productName === ProductNames.PACIM && this.platformService?.lineOfBusiness === LineOfBusiness.Commercial
      : productName === ProductNames.PacCommercial;
    
    if (isCommercial) {
      return this.checkACMPApprovalLetter(documents, documentsContextService, documentTypes, servicingProvider);
    } else {
      return this.checkNomncRescindOption(authorizationStatus, documentTypes, createContinuedStayRequest);
    }
  }

  checkNomncRescindOption(authorizationStatus: string, documentTypes: IDocumentReferenceProductDTO[], createContinuedStayRequest: boolean): Observable<IDocumentReferenceProductDTO[]> {
    return authorizationStatus !== 'Active' || !createContinuedStayRequest
      ? of(documentTypes.filter(document => document.description !== this.nomncRescind))
      : of(documentTypes);
  }

  checkACMPApprovalLetter(
    documents: IDocumentResponseDTO[],
    documentsContextService: DocumentsContextService,
    documentTypes: IDocumentReferenceProductDTO[],
    servicingProvider: any,
  ): Observable<IDocumentReferenceProductDTO[]> {
    const approvalLetterACMP = documents
      .sort((firstDoc, secondDoc) => new Date(secondDoc.dateLetterCreated).getTime() - new Date(firstDoc.dateLetterCreated).getTime());

    const isLevelReimbursementType = servicingProvider?.providerPgpDTOList[0]?.reimbursementType === 'Level';

    if (approvalLetterACMP[0]?.dateLetterCreated
      && approvalLetterACMP[0].documentType === 'Approval'
      && approvalLetterACMP[0].communicationType === "Letter"
      && isLevelReimbursementType) {
      const lastApprovalLetterACMPDate = new Date(approvalLetterACMP[0].dateLetterCreated);

      return documentsContextService.getCalendarDay(lastApprovalLetterACMPDate.toDateString(), this.businessDays, true)
      .pipe(
        map(res => {
          if (res) {
            const dateTimeToOverdue = new Date(res.calendarDate);
            dateTimeToOverdue.setHours(0, 0, 0, 0);

            const currentDateTime = new Date(new Date().toLocaleString('en-US', {timeZone: 'CST'}));
            currentDateTime.setHours(0, 0, 0, 0);

            this.isACMPApprovalLetterOverdue = currentDateTime > dateTimeToOverdue;

            return this.isACMPApprovalLetterOverdue
              ? documentTypes.filter(document => document.description !== this.levelAdjustment)
              : documentTypes
          }

          return documentTypes;
        })
      )
    }
    return of(documentTypes);
  }

  override setSelectionAbility(data: string, selectedTypes: AbstractControl): boolean {
    const selected = selectedTypes?.value;

    if (!selected?.length) {
      return false
    }

    return selected.includes(this.nomncRescind) ? data !== this.nomncRescind : data === this.nomncRescind;
  }

  override forceContinuedStayRequest(selected: string[], selectedTypes: AbstractControl): boolean {
    if (selected.length > 1 && selected.includes(this.nomncRescind)) {
      selected = selected.filter(item => item !== this.nomncRescind);

      selectedTypes.setValue(selected);
    }

    return selected.includes(this.nomncRescind);
  }

  override checkContinuedStayEligibility(authorizationByProductId: string): Observable<ToastMessageOptions> {
    const continuedStayMessage: ToastMessageOptions = {};

    return this.http.get<ToastMessageOptions>(`${this.API_URL}/tenants/${this.shellService.getTenantId()}/authorization/${authorizationByProductId}/continue/eligible`)
      .pipe(
        map(() => {
          continuedStayMessage.severity = 'info';
          continuedStayMessage.detail = ChooseActionInfoMessage;

          return continuedStayMessage;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.error?.report?.code === 'General-139.1') {
            continuedStayMessage.severity = 'error';
            continuedStayMessage.summary = 'Submission Not Yet Available';
            continuedStayMessage.detail = AdditionalDaysRequestPendingSL;
          } else if (error.error?.report?.code === 'General-139.2') {
            continuedStayMessage.severity = 'error';
            continuedStayMessage.summary = 'Submission Not Yet Available';
            continuedStayMessage.detail = ContinuedStayRequestTwoMoreDaysSL;
          } else {
            continuedStayMessage.severity = 'error';
            continuedStayMessage.summary = 'Error';
            continuedStayMessage.detail = SomethingWentWrong;
          }

          return of(continuedStayMessage);
        })
      );
  }
}
